import { calculateDiscount } from "../../utils/calculateDiscount.js";
import { roundToPlaces } from "../../utils/roundToPlaces.js";
import type { Country } from "../Country.js";
import { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { ProductForCustomer } from "../Product.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import type { SubscriptionParamsForPricing } from "../Subscription.js";
import { SubscriptionSize } from "../SubscriptionSize.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { calculatePriceOfSubscriptionWithoutDiscounts } from "./calculatePriceOfSubscriptionWithoutDiscounts.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { calculatePriceWithoutTax } from "./calculatePriceWithoutTax.js";
import { membershipPricePerCurrencyMap } from "./membershipPricePerCurrencyMap.js";
import { membershipTaxRate } from "./membershipTaxRate.js";

export function calculatePriceOfRecurringSubscription({
	subcategories,
	destinationCountry,
	subscription,
	mothersBlendProduct,
	cafeinaProduct,
	currency,
	vatType,
	exchangeRates,
	mothersBlendConstantPriceEnabled,
}: {
	subcategories: SubcategoryForCustomer[];
	destinationCountry: Country | "outsideEu";
	subscription: SubscriptionParamsForPricing;
	mothersBlendProduct: ProductForCustomer;
	cafeinaProduct: ProductForCustomer;
	currency: Currency;
	vatType: EconomicSubjectVatType;
	exchangeRates: ExchangeRate[];
	mothersBlendConstantPriceEnabled: boolean;
}): TaxedPrice {
	const base = calculatePriceOfSubscriptionWithoutDiscounts({
		subcategories,
		destinationCountry,
		subscription,
		mothersBlendProduct,
		cafeinaProduct,
		currency,
		vatType,
		exchangeRates,
		mothersBlendConstantPriceEnabled,
		deliveryCount: undefined,
	});

	const discount = {
		[SubscriptionSize.G250x2]: 15,
		[SubscriptionSize.G250]: 5,
		[SubscriptionSize.Kg1]: 5,
	}[subscription.size];

	const membershipPriceWithTax = membershipPricePerCurrencyMap[currency];
	const membershipPriceWithoutTax = calculatePriceWithoutTax(membershipPriceWithTax, membershipTaxRate);

	let withoutTax = roundToPlaces(calculateDiscount(base.withoutTax, discount), 2);
	let withTax = calculatePriceWithTax(withoutTax - membershipPriceWithoutTax, base.taxRate) + membershipPriceWithTax;

	if (currency === Currency.CZK) {
		withTax = Math.ceil(withTax);
		withoutTax = calculatePriceWithoutTax(withTax, base.taxRate);
	}

	return {
		taxRate: base.taxRate,
		withTax,
		withoutTax,
	};
}
